// Homepage content blocks (English source content).

export const MANIFESTO = {
  eyebrow: "Understand your options before travelling",
  lines: [
    "We help you slow down,",
    "ask the correct questions,",
    "understand the risks,",
    "and compare suitable options.",
  ],
};

export const HOW_WE_HELP = {
  eyebrow: "How we help",
  heading: "Clear support before you make a decision",
  intro:
    "We coordinate information and communication. Doctors make all clinical decisions. An initial opinion may change after examination, and you remain free to proceed or not.",
  steps: [
    {
      n: "01",
      title: "Tell Us What You Are Considering",
      text: "Share your goals and any relevant history so we understand your situation.",
    },
    {
      n: "02",
      title: "Initial Case Review",
      text: "Our team reviews your enquiry and explains the appropriate next steps.",
    },
    {
      n: "03",
      title: "Suitable Provider or Surgeon Review",
      text: "We help identify appropriately qualified professionals for your case.",
    },
    {
      n: "04",
      title: "Direct Consultation & Treatment Planning",
      text: "You speak directly with medical professionals about a proposed treatment option.",
    },
    {
      n: "05",
      title: "Travel & Appointment Coordination",
      text: "We help organise appointments, timing and practical arrangements.",
    },
    {
      n: "06",
      title: "Recovery & Aftercare Communication",
      text: "We stay in touch to support communication during your recovery.",
    },
  ],
};

export const TRUST_SAFETY = {
  eyebrow: "Trust & safety",
  heading: "Trust should be based on evidence",
  intro:
    "We do not display generic accreditation badges. Any registration or membership is only shown once verified for the specific hospital, surgeon, product or device.",
  link: "How to check a plastic surgeon's credentials in Vietnam",
  linkTo: "/how-to-choose-a-plastic-surgeon-in-vietnam",
  items: [
    { icon: "ShieldCheck", title: "Provider licence review" },
    { icon: "BadgeCheck", title: "Surgeon credential review" },
    { icon: "Building2", title: "Hospital or operating-location confirmation" },
    { icon: "ClipboardList", title: "Clear treatment-plan information" },
    { icon: "ReceiptText", title: "Transparent quotation guidance" },
    { icon: "Lock", title: "Patient privacy" },
    { icon: "Languages", title: "Multilingual support" },
    { icon: "HeartPulse", title: "Recovery planning" },
  ],
};

export const WHY_VIETNAM = {
  eyebrow: "Why Vietnam",
  heading: "Why international patients consider Vietnam",
  intro:
    "A balanced, factual view. Vietnam is not automatically safer or cheaper, and results are never guaranteed. Standards vary between facilities and surgeons.",
  link: "Vietnam, Thailand or South Korea: what should patients compare?",
  linkTo: "/plastic-surgery-vietnam-vs-thailand",
  points: [
    "Experienced surgeons in selected specialities",
    "Modern private hospitals and treatment facilities",
    "Competitive treatment costs compared with some Western countries",
    "International patient services",
    "Established tourism and accommodation infrastructure",
    "Suitable destinations for a planned recovery period",
  ],
};

export const PATIENT_JOURNEY = {
  eyebrow: "The patient journey",
  heading: "Your journey, explained before you travel",
  note:
    "Treatment plans can change after examination, pricing may change if clinical findings differ, and the length of stay depends on the operation and recovery. Surgery is never presented as guaranteed.",
  steps: [
    "Enquiry",
    "Information and photographs",
    "Initial provider review",
    "Online consultation",
    "Treatment plan and quotation",
    "Decision and booking",
    "Travel preparation",
    "In-person consultation and medical tests",
    "Treatment",
    "Follow-up and recovery",
    "Communication after returning home",
  ],
};

export const GUIDE_PREVIEWS = {
  eyebrow: "Guides",
  heading: "Safety, costs and choosing a surgeon",
  cards: [
    {
      title: "Is Plastic Surgery in Vietnam Safe?",
      text: "Safety depends on the individual surgeon, facility, anaesthesia, your health, the procedure and aftercare — not simply the country.",
      to: "/plastic-surgery-safety-vietnam",
      anchor: "Read our guide to plastic surgery safety in Vietnam",
    },
    {
      title: "Plastic Surgery Costs in Vietnam",
      text: "Cost depends on procedure complexity, the surgeon, the facility, anaesthesia, implants, hospital nights, tests, garments, medication, follow-up and any revision.",
      to: "/plastic-surgery-cost-vietnam",
      anchor: "Read our guide to plastic surgery costs in Vietnam",
    },
    {
      title: "How to Choose a Plastic Surgeon in Vietnam",
      text: "Learn how to check licensing, procedure experience, the operating facility, anaesthesia, results, follow-up and the warning signs to avoid.",
      to: "/how-to-choose-a-plastic-surgeon-in-vietnam",
      anchor: "Learn how to choose a plastic surgeon in Vietnam",
    },
  ],
};

export const REVISION = {
  eyebrow: "Revision & corrective surgery",
  heading: "Considering revision or corrective surgery?",
  text:
    "Revision cases may require more detailed assessment, previous medical records, implant information, photographs and an in-person examination. We help organise the information needed for an appropriate professional review.",
  note: "We cannot promise that every revision case can be accepted.",
  examples: [
    "Previous rhinoplasty",
    "Eyelid scar revision",
    "Breast implant problems",
    "Capsular contracture",
    "Implant removal or replacement",
    "Abdominal or body-contouring revision",
    "Scar correction",
    "Facial filler or silicone complications",
  ],
};

export const SUPPORT = {
  eyebrow: "International patient support",
  heading: "Practical support for your treatment journey",
  note:
    "Travel, accommodation, translation, transport and nursing may be delivered by third parties and are confirmed according to your individual case.",
  cards: [
    "Online consultation coordination",
    "English-speaking support",
    "French and German communication",
    "Appointment scheduling",
    "Visa and travel guidance",
    "Airport transfer coordination",
    "Accommodation guidance",
    "Translation support",
    "Optional nurse or recovery support",
    "Follow-up communication",
  ],
};

export const RESULTS = {
  eyebrow: "Results & patient stories",
  heading: "Real experiences. Realistic expectations.",
  statement:
    "Patient stories and before-and-after results will be published only with consent and appropriate clinical context. Individual results vary.",
  rules: [
    "We use only content shared with patient consent.",
    "We do not digitally alter clinical results.",
    "We identify the procedure and approximate recovery stage.",
    "Results vary between patients and are never a guarantee.",
  ],
  // Gallery categories reserved for future approved content (kept hidden until available).
  galleryTypes: ["Patient stories", "Before-and-after galleries", "Recovery experiences", "Video testimonials"],
};

export const FINAL_CTA = {
  eyebrow: "Understand your options first",
  heading: "Start a confidential case review",
  text:
    "Share what you are considering and receive guidance on the information, consultations and next steps that may be required before making travel arrangements.",
  trust: [
    "Private and confidential",
    "No obligation to proceed",
    "Clinical recommendations come from licensed medical professionals",
  ],
};
